import React, {useEffect, useState} from 'react';
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Tag} from "primereact/tag";
import {Checkbox} from "primereact/checkbox";
import {InputNumber} from "primereact/inputnumber";
import {MultiSelect} from "primereact/multiselect";
import {Dropdown} from "primereact/dropdown";
import {Message} from "primereact/message";

import {AppContext} from "../../HomePage";
import {CampaignContext} from "../../App";
import {CampaignService} from "../../service/CampaignService";
import {scrapy_cloud_projects} from "../../utils/Constants";
import {scheduleRowToLocal, scheduleRowToUtc, localTzLabel} from "../../utils/TimeUtil";
import DaySelector from "./DaySelector";
import JobSchedulingWebSearchSetting from "../Admin/JobSchedulingWebSearchSetting";
import JobSchedulingDailyWebSearchSetting from "../Admin/JobSchedulingDailyWebSearchSetting";
import JobSchedulingSiteSearchSetting from "../Admin/JobSchedulingSiteSearchSetting";
import JobSchedulingDailySiteSearchSetting from "../Admin/JobSchedulingDailySiteSearchSetting";
import JobSchedulingNewCompetitorWebSearchSetting from "../Admin/JobSchedulingNewCompetitorWebSearchSetting";
import {HelpIcon} from "./OnboardingHelp";

const campaignService = new CampaignService();

const pad = (n) => String(n === null || n === undefined ? 0 : n).padStart(2, '0');
const clone = (o) => JSON.parse(JSON.stringify(o || null));

// Make the schedule rows match the Daily Price Checks count: trim extras, or
// add rows +5h from the last one inheriting the current behaviour flags.
// Mirrors the legacy server behaviour (CampaignService.add_new_scraping_jobs).
const syncRows = (rows, count) => {
    const next = rows.slice(0, count);
    const tpl = rows[0] || {};
    let hh = next.length ? next[next.length - 1].hh : 19;
    const mm = next.length ? next[next.length - 1].mm : 30;
    while (next.length < count) {
        hh = (hh + 5) % 24;
        next.push({
            hh, mm, job_days: [],
            auto_price_update: tpl.auto_price_update ?? true,
            notify: tpl.notify ?? true,
            auto_suggested_price_update: tpl.auto_suggested_price_update ?? true,
            headless_browser_enabled: !!tpl.headless_browser_enabled,
        });
    }
    return next;
};

const Lbl = ({text, help, onL, offL}) => (
    <label style={{display: 'flex', alignItems: 'center', gap: 4, fontSize: '.8rem', color: '#6b7280', marginBottom: 2}}>
        {text}<HelpIcon text={help} onL={onL} offL={offL}/>
    </label>
);

const DAYS = [
    {value: 0, label: 'Mon'}, {value: 1, label: 'Tue'}, {value: 2, label: 'Wed'},
    {value: 3, label: 'Thu'}, {value: 4, label: 'Fri'}, {value: 5, label: 'Sat'},
    {value: 6, label: 'Sun'}, {value: 7, label: 'Never'},
];
const JOB_PRIORITIES = [{value: 0, label: 'Lowest'}, {value: 1, label: 'Low'}, {value: 2, label: 'Normal'},
    {value: 3, label: 'High'}, {value: 4, label: 'Highest'}];

// A schedule row runs if its days are empty (every day) or include a real
// weekday (0-6). job_days === [7] (Never) means it runs on no day.
const runsSomeDay = (jd) => !jd || jd.length === 0 || jd.some(d => d >= 0 && d <= 6);
const dayLabels = (jd) => (jd || []).filter(d => d >= 0 && d <= 6)
    .map(d => (DAYS.find(x => x.value === d) || {}).label).filter(Boolean);
const priorityLabel = (v) => (JOB_PRIORITIES.find(p => p.value === (v === null || v === undefined ? 2 : v)) || {}).label;

// All search jobs (key + display + scheduling mode).
const SEARCH_JOBS = [
    {key: 'web_search_job', labelKey: 'campaign.web_search_setting', icon: 'pi pi-search', isDaily: false, isGS: false},
    {key: 'google_shopping_search_job', labelKey: 'campaign.google_shopping_search_setting', icon: 'pi pi-shopping-cart', isDaily: false, isGS: true},
    {key: 'daily_web_search_job', labelKey: 'campaign.daily_web_search_setting', icon: 'pi pi-search', isDaily: true, isGS: false},
    {key: 'daily_google_shopping_search_job', labelKey: 'campaign.daily_google_shopping_search_setting', icon: 'pi pi-shopping-cart', isDaily: true, isGS: true},
    {key: 'site_search_job', labelKey: 'campaign.site_search_setting', icon: 'pi pi-globe', isDaily: false, isGS: false, site: true},
    {key: 'daily_site_search_job', labelKey: 'campaign.daily_site_search_setting', icon: 'pi pi-globe', isDaily: true, isGS: false, site: true},
    {key: 'new_competitor_web_search_job', labelKey: 'campaign.new_competitor_web_search_setting', icon: 'pi pi-globe', isDaily: false, isGS: false},
];

// Render the existing full settings tab (Time + options + competitor/seller
// settings) for a job, driven by a draft campaign it mutates in place.
const renderJobComponent = (key, draftCampaign) => {
    switch (key) {
        case 'web_search_job': return <JobSchedulingWebSearchSetting campaign={draftCampaign} isGoogleShoppingSearch={false}/>;
        case 'google_shopping_search_job': return <JobSchedulingWebSearchSetting campaign={draftCampaign} isGoogleShoppingSearch={true}/>;
        case 'daily_web_search_job': return <JobSchedulingDailyWebSearchSetting campaign={draftCampaign} isGoogleShoppingSearch={false}/>;
        case 'daily_google_shopping_search_job': return <JobSchedulingDailyWebSearchSetting campaign={draftCampaign} isGoogleShoppingSearch={true}/>;
        case 'site_search_job': return <JobSchedulingSiteSearchSetting campaign={draftCampaign}/>;
        case 'daily_site_search_job': return <JobSchedulingDailySiteSearchSetting campaign={draftCampaign}/>;
        case 'new_competitor_web_search_job': return <JobSchedulingNewCompetitorWebSearchSetting campaign={draftCampaign}/>;
        default: return null;
    }
};

const ScheduledJobsOnboardingStep = ({t, refresh, embedded}) => {
    const {toast, setError} = React.useContext(AppContext);
    const {selectedCampaign, loadCampaigns} = React.useContext(CampaignContext);
    const campaignId = selectedCampaign?.id;
    // Marketplace API campaigns configure refresh cadence via the dedicated
    // "Price Check" onboarding step (MarketplacePriceCheckStep, e.g. "every 4
    // hours"), not the legacy scraping schedule below -- showing both is
    // contradictory (Task 21). Keep this dialog available for marketplace
    // campaigns, but hide only the Schedule rows and the Daily Price Checks
    // count; Behavior and the rest of Job Settings stay visible (auto-reprice
    // still resolves a job from scraping_jobs/daily_price_check_count, which
    // stay loaded in state below and are saved unchanged -- see savePrice).
    const isMarketplaceApi = !!selectedCampaign?.settings?.marketplace_api;
    const onL = t('onboarding.settings.onLabel');
    const offL = t('onboarding.settings.offLabel');

    const [settings, setSettings] = useState(() => clone(selectedCampaign?.settings) || {});
    const [editing, setEditing] = useState(null); // {type:'search'|'price', ...jobCfg}
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);

    // Price-check rows are server-provisioned; load them into settings.scraping_jobs.
    useEffect(() => {
        if (!campaignId) return;
        if (settings.scraping_jobs && settings.scraping_jobs.length) return;
        campaignService.scrapingJobHours(campaignId)
            .then(rows => setSettings(prev => ({...prev, scraping_jobs: rows || []})))
            .catch(() => {});
    }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

    const hourStr = (h) => h.time || (pad(h.hour) + ':' + pad(h.minute));

    const searchSummary = (cfg) => {
        const obj = settings[cfg.key] || {};
        const enabled = cfg.site ? (obj.is_searching_enabled || obj.is_site_searching_enabled) : obj.is_searching_enabled;
        const hours = obj.hours || [];
        // Active only when enabled AND it has at least one schedule entry.
        if (!enabled) return {active: false, text: t('onboarding.scheduledJobs.inactive')};
        if (!hours.length) return {active: false, text: t('onboarding.scheduledJobs.noSchedule')};

        let schedule;
        if (cfg.isDaily) {
            schedule = t('onboarding.scheduledJobs.daily') + ' ' + hours.map(hourStr).join(', ');
        } else {
            // Monthly: day of month + time.
            schedule = hours.map(h => t('onboarding.scheduledJobs.monthlyDay')
                .replace('{day}', h.day === null || h.day === undefined ? '?' : h.day)
                .replace('{time}', hourStr(h))).join(', ');
        }
        let text = schedule + ' (UTC)';
        if (cfg.site) {
            const urls = obj.competitor_urls || [];
            if (urls.length) text += '. ' + t('onboarding.scheduledJobs.siteSearchSites').replace('{sites}', urls.join(', '));
        }
        return {active: true, text};
    };

    const priceSummary = () => {
        // Marketplace API campaigns: the schedule/priority/units string below is
        // all legacy scraping vocabulary (job units are Zyte units) built from
        // the same 24 hardcoded hourly rows the dialog hides fields for (see
        // isMarketplaceApi above) -- rendering it here would show the identical
        // "24 hourly" contradiction on the card, one screen before the admin
        // even opens the dialog. Return a neutral line instead; the card stays
        // clickable (onClick={openPrice} below is untouched) and the dialog it
        // opens still shows Behavior + the remaining Job Settings.
        if (isMarketplaceApi) return {active: true, text: t('onboarding.scheduledJobs.marketplaceSummary')};
        const rows = settings.scraping_jobs || [];
        // Effective = auto price update on AND it actually runs on some day.
        const eff = rows.filter(r => r.auto_price_update && runsSomeDay(r.job_days));
        if (!eff.length) return {active: false, text: t('onboarding.scheduledJobs.inactive')};
        const schedule = eff.map(r => {
            const lr = scheduleRowToLocal(r);   // UTC → yerel gösterim
            const labels = dayLabels(lr.job_days);
            const days = labels.length ? labels.join(', ') : t('onboarding.scheduledJobs.everyDay');
            return t('onboarding.scheduledJobs.scheduleSeg').replace('{days}', days).replace('{time}', pad(lr.hh) + ':' + pad(lr.mm));
        }).join('; ');
        const text = t('onboarding.scheduledJobs.priceSummary')
            .replace('{priority}', priorityLabel(settings.job_priority))
            .replace('{units}', settings.job_units ?? 1)
            .replace('{divided}', settings.divided_job_units ?? 1)
            .replace('{schedule}', schedule);
        return {active: true, text};
    };

    const openSearch = (cfg) => {
        // Draft campaign whose settings the full tab component mutates in place.
        const draftCampaign = {...selectedCampaign, settings: clone(settings) || {}};
        setDraft(draftCampaign);
        setEditing({type: 'search', ...cfg});
    };

    const openPrice = () => {
        // Saatler UTC saklanır; kullanıcıya yerel saat diliminde gösterilir.
        let rows = (clone(settings.scraping_jobs) || []).map(scheduleRowToLocal);
        // Reconcile rows with a previously-saved Daily Price Checks count (e.g.
        // count=3 but only 1 row stored) so the schedule reflects the count.
        const count = settings.daily_price_check_count;
        if (count) rows = syncRows(rows, count);
        setDraft({
            scraping_jobs: rows,
            job_units: settings.job_units ?? 1,
            divided_job_units: settings.divided_job_units ?? 1,
            job_priority: settings.job_priority ?? 2,
            daily_price_check_count: settings.daily_price_check_count ?? null,
            projects_used_for_price_update: settings.projects_used_for_price_update ?? [scrapy_cloud_projects[0].value],
            price_check_for_also_unavailable_urls: !!settings.price_check_for_also_unavailable_urls,
            price_check_for_also_deactivated_urls: !!settings.price_check_for_also_deactivated_urls,
            scape_price_in_cart: !!settings.scape_price_in_cart,
            amazon_shipping_price_included: !!settings.amazon_shipping_price_included,
            rerun_failed_job: !!settings.rerun_failed_job,
            rerun_failed_job_number: settings.rerun_failed_job_number ?? 1,
            rerun_failed_job_delay: settings.rerun_failed_job_delay ?? 0,
        });
        setEditing({type: 'price'});
    };

    const close = () => { if (!saving) { setEditing(null); setDraft(null); } };

    const persist = (merged) => {
        setSaving(true);
        campaignService.saveCampaignSettings(campaignId, selectedCampaign.use_sale_value, merged).then(() => {
            selectedCampaign.settings = merged;
            setSettings(clone(merged));
            if (loadCampaigns) loadCampaigns();
            toast.current.show({severity: 'success', summary: t('onboarding.scheduledJobs.saved'), detail: t('message_detail.successful')});
            if (refresh) refresh();
            setEditing(null);
            setDraft(null);
        }).catch(error => {
            setError(error);
            setError(null);
        }).finally(() => setSaving(false));
    };

    const saveSearch = () => persist(draft.settings);

    const savePrice = () => {
        const rows = draft.scraping_jobs.map(r => {
            // Yerel saat → UTC'ye geri çevir (gün kaydırması dahil).
            const utc = scheduleRowToUtc({...r, job_days: r.job_days || []});
            return {...utc,
                auto_price_update: !!r.auto_price_update,
                notify: !!r.notify,
                auto_suggested_price_update: !!r.auto_suggested_price_update,
                headless_browser_enabled: !!r.headless_browser_enabled,
            };
        });
        persist({
            ...settings,
            scraping_jobs: rows,
            job_units: draft.job_units,
            divided_job_units: draft.divided_job_units,
            job_priority: draft.job_priority,
            daily_price_check_count: draft.daily_price_check_count,
            projects_used_for_price_update: draft.projects_used_for_price_update,
            price_check_for_also_unavailable_urls: !!draft.price_check_for_also_unavailable_urls,
            price_check_for_also_deactivated_urls: !!draft.price_check_for_also_deactivated_urls,
            scape_price_in_cart: !!draft.scape_price_in_cart,
            amazon_shipping_price_included: !!draft.amazon_shipping_price_included,
            rerun_failed_job: !!draft.rerun_failed_job,
            rerun_failed_job_number: draft.rerun_failed_job_number,
            rerun_failed_job_delay: draft.rerun_failed_job_delay,
        });
    };

    const updateRow = (i, key, value) => setDraft(prev => {
        const rows = prev.scraping_jobs.slice();
        rows[i] = {...rows[i], [key]: value};
        return {...prev, scraping_jobs: rows};
    });
    const setD = (key, value) => setDraft(prev => ({...prev, [key]: value}));
    // Changing Daily Price Checks adds/removes schedule rows to match the count
    // (mirrors the legacy server behaviour: each extra row is +5h from the last,
    // inheriting the current behaviour flags).
    const setDailyCount = (value) => {
        if (value === null || value === undefined) { setD('daily_price_check_count', value); return; }
        setDraft(prev => ({...prev, daily_price_check_count: value, scraping_jobs: syncRows(prev.scraping_jobs, Math.max(0, value))}));
    };
    // Price-check flags apply to all schedule rows (read from the first).
    const flagValue = (f) => (draft && draft.scraping_jobs.length ? !!draft.scraping_jobs[0][f] : false);
    const setFlagAll = (f, v) => setDraft(prev => ({...prev, scraping_jobs: prev.scraping_jobs.map(r => ({...r, [f]: v}))}));

    const Card = ({icon, title, summary, onClick}) => (
        <div onClick={onClick} style={{cursor: 'pointer', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff',
            display: 'flex', flexDirection: 'column', gap: 8, transition: 'box-shadow .15s'}}
             onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,.08)'}
             onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8}}>
                <span style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8}}>
                    <i className={icon} style={{color: '#2563eb'}}/> {title}
                </span>
                <Tag value={summary.active ? t('onboarding.scheduledJobs.active') : t('onboarding.scheduledJobs.inactive')}
                     severity={summary.active ? 'success' : 'danger'}/>
            </div>
            <small style={{color: '#6b7280'}}>{summary.text}</small>
            <small style={{color: '#9ca3af'}}>{t('onboarding.scheduledJobs.clickToConfigure')}</small>
        </div>
    );

    return (
        <div>
            {!embedded && <>
                <h2 style={{margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 10}}>
                    <i className="pi pi-clock"/> {t('onboarding.step.scheduled_jobs.label', 'Zamanlanmış İşler')}
                </h2>
                <p style={{color: '#6b7280', maxWidth: 640, marginTop: 0}}>{t('onboarding.step.scheduled_jobs.desc')}</p>
            </>}

            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14, marginTop: 8}}>
                <Card icon="pi pi-tags" title={t('onboarding.scheduledJobs.priceCheck')} summary={priceSummary()} onClick={openPrice}/>
                {SEARCH_JOBS.map(cfg => (
                    <Card key={cfg.key} icon={cfg.icon} title={t(cfg.labelKey)} summary={searchSummary(cfg)} onClick={() => openSearch(cfg)}/>
                ))}
            </div>

            {/* Search-job config popup (reuses the existing settings widgets). */}
            <Dialog header={editing && editing.type === 'search' ? t(editing.labelKey) : ''}
                    visible={!!editing && editing.type === 'search'} style={{width: '820px', maxWidth: '96vw'}} modal
                    onHide={close}
                    footer={
                        <div>
                            <Button label={t('actions.cancel')} className="p-button-text" disabled={saving} onClick={close}/>
                            <Button label={t('actions.save')} icon="pi pi-save" loading={saving} disabled={saving} onClick={saveSearch}/>
                        </div>
                    }>
                {editing && editing.type === 'search' && draft && (
                    <div key={editing.key}>
                        {renderJobComponent(editing.key, draft)}
                    </div>
                )}
            </Dialog>

            {/* Price-check config popup. */}
            <Dialog header={t('onboarding.scheduledJobs.priceCheck')}
                    visible={!!editing && editing.type === 'price'} style={{width: '820px', maxWidth: '96vw'}} modal
                    onHide={close}
                    footer={
                        <div>
                            <Button label={t('actions.cancel')} className="p-button-text" disabled={saving} onClick={close}/>
                            <Button label={t('actions.save')} icon="pi pi-save" loading={saving} disabled={saving} onClick={savePrice}/>
                        </div>
                    }>
                {editing && editing.type === 'price' && draft && (
                    <div>
                        {/* Hidden for marketplace API campaigns together with the Schedule
                            rows below (see isMarketplaceApi above) -- with no hour/minute rows
                            left visible in this dialog, a hint about setting hours/days would
                            be orphaned. */}
                        {!isMarketplaceApi && (
                        <small style={{display: 'block', color: '#6b7280', marginBottom: 12}}>{t('onboarding.scheduledJobs.priceCheckHint')}</small>
                        )}

                        {/* Times are shown in the user's local timezone; stored as UTC in the
                            DB. Hidden for marketplace API campaigns together with the Schedule
                            rows below (see isMarketplaceApi above) -- with no hour/minute
                            values left visible in this dialog, a note about how those hours
                            are displayed would be orphaned. */}
                        {!isMarketplaceApi && (
                        <Message severity="info" style={{width: '100%', marginBottom: 14}}
                                 text={t('onboarding.scheduledJobs.tzNote').replace('{tz}', localTzLabel())}/>
                        )}

                        {/* Group 1: Schedule -- hidden for marketplace API campaigns (see
                            isMarketplaceApi above); draft.scraping_jobs stays loaded in state
                            either way and savePrice() below still sends it unchanged. */}
                        {!isMarketplaceApi && (
                        <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginBottom: 14}}>
                            <div style={{fontWeight: 700, marginBottom: 10}}>{t('onboarding.scheduledJobs.grpSchedule')}</div>
                            {draft.scraping_jobs.length === 0
                                ? <p style={{color: '#9ca3af', margin: 0}}>{t('onboarding.scheduledJobs.noPriceRows')}</p>
                                : draft.scraping_jobs.map((r, i) => (
                                    <div key={i} style={{display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end',
                                        paddingTop: i ? 10 : 0, marginTop: i ? 10 : 0, borderTop: i ? '1px solid #f1f5f9' : 'none'}}>
                                        <span>
                                            <Lbl text={t('job.hour')} help={t('onboarding.scheduledJobs.help.hour')}/>
                                            <InputNumber value={r.hh} min={0} max={23} showButtons buttonLayout="horizontal"
                                                         inputStyle={{width: 56, textAlign: 'center'}}
                                                         incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                                         onValueChange={(e) => updateRow(i, 'hh', e.value ?? 0)}/>
                                        </span>
                                        <span>
                                            <Lbl text={t('job.minute')} help={t('onboarding.scheduledJobs.help.minute')}/>
                                            <InputNumber value={r.mm} min={0} max={59} step={10} showButtons buttonLayout="horizontal"
                                                         inputStyle={{width: 56, textAlign: 'center'}}
                                                         incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                                         onValueChange={(e) => updateRow(i, 'mm', e.value ?? 0)}/>
                                        </span>
                                        <span style={{flex: '1 1 100%'}}>
                                            <Lbl text={t('job.days')} help={t('onboarding.scheduledJobs.help.days')}/>
                                            <DaySelector value={r.job_days} onChange={(v) => updateRow(i, 'job_days', v)} t={t}/>
                                        </span>
                                    </div>
                                ))}
                        </div>
                        )}

                        {/* Group 2: Behavior -- for marketplace API campaigns only the three
                            meaningful checkboxes render (Auto Price Check, Notify, Auto
                            Suggested Price Update); the rest are scraping-only (headless
                            browser, cart scraping, unavailable-URL checks, retry policy) and
                            are hidden (see isMarketplaceApi above). Each flag's only mutator is
                            its own onChange (setFlagAll/setD), so hiding a checkbox never
                            touches its draft value -- savePrice() below still sends every flag
                            (draft.scraping_jobs behavior flags and
                            draft.price_check_for_also_unavailable_urls /
                            price_check_for_also_deactivated_urls / scape_price_in_cart /
                            amazon_shipping_price_included / rerun_failed_job) unchanged. */}
                        <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginBottom: 14}}>
                            <div style={{fontWeight: 700, marginBottom: 10}}>{t('onboarding.scheduledJobs.grpBehavior')}</div>
                            <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px 18px'}}>
                                {['auto_price_update', 'notify', 'auto_suggested_price_update', 'headless_browser_enabled']
                                    .filter(f => !isMarketplaceApi || f !== 'headless_browser_enabled')
                                    .map(f => (
                                    <span key={f} style={{display: 'flex', alignItems: 'center', gap: 6}}>
                                        <Checkbox inputId={f} checked={flagValue(f)} onChange={(e) => setFlagAll(f, e.checked)}/>
                                        <label htmlFor={f} style={{margin: 0}}>{t('job.' + f)}</label>
                                        <HelpIcon text={t('onboarding.scheduledJobs.help.' + f)} onL={onL} offL={offL}/>
                                    </span>
                                ))}
                                {!isMarketplaceApi && ['price_check_for_also_unavailable_urls', 'price_check_for_also_deactivated_urls', 'scape_price_in_cart', 'amazon_shipping_price_included', 'rerun_failed_job'].map(f => (
                                    <span key={f} style={{display: 'flex', alignItems: 'center', gap: 6}}>
                                        <Checkbox inputId={f} checked={!!draft[f]} onChange={(e) => setD(f, e.checked)}/>
                                        <label htmlFor={f} style={{margin: 0}}>{t('campaign.' + f)}</label>
                                        <HelpIcon text={t('onboarding.scheduledJobs.help.' + f)} onL={onL} offL={offL}/>
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Group 3: Job Settings -- entirely scraping-only tuning (job
                            units/priority, scrapy projects, rerun policy); hidden as a whole
                            for marketplace API campaigns (see isMarketplaceApi above). Every
                            field below is mutated only by setD/setDailyCount, so hiding the
                            section never touches draft.job_units, divided_job_units,
                            job_priority, daily_price_check_count,
                            projects_used_for_price_update, rerun_failed_job_number or
                            rerun_failed_job_delay -- savePrice() below still sends them all
                            unchanged. */}
                        {!isMarketplaceApi && (
                        <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 14}}>
                            <div style={{fontWeight: 700, marginBottom: 10}}>{t('onboarding.scheduledJobs.grpTuning')}</div>
                            <div style={{display: 'flex', flexWrap: 'wrap', gap: 14}}>
                                <span style={{flex: '1 1 160px'}}>
                                    <Lbl text={t('campaign.job_units')} help={t('onboarding.scheduledJobs.help.job_units')} onL={onL} offL={offL}/>
                                    <InputNumber value={draft.job_units} style={{width: '100%'}} onValueChange={(e) => setD('job_units', e.value)}/>
                                </span>
                                <span style={{flex: '1 1 160px'}}>
                                    <Lbl text={t('campaign.divided_job_units')} help={t('onboarding.scheduledJobs.help.divided_job_units')} onL={onL} offL={offL}/>
                                    <InputNumber value={draft.divided_job_units} style={{width: '100%'}} onValueChange={(e) => setD('divided_job_units', e.value)}/>
                                </span>
                                <span style={{flex: '1 1 160px'}}>
                                    <Lbl text={t('campaign.job_priority')} help={t('onboarding.scheduledJobs.help.job_priority')} onL={onL} offL={offL}/>
                                    <Dropdown value={draft.job_priority} options={JOB_PRIORITIES} style={{width: '100%'}} onChange={(e) => setD('job_priority', e.value)}/>
                                </span>
                                {/* Hidden for marketplace API campaigns (see isMarketplaceApi
                                    above); draft.daily_price_check_count stays loaded in state
                                    either way and savePrice() below still sends it unchanged. */}
                                {!isMarketplaceApi && (
                                <span style={{flex: '1 1 160px'}}>
                                    <Lbl text={t('subscription.daily_price_check_count')} help={t('onboarding.scheduledJobs.help.daily_price_check_count')} onL={onL} offL={offL}/>
                                    <InputNumber value={draft.daily_price_check_count} min={0} showButtons style={{width: '100%'}} onValueChange={(e) => setDailyCount(e.value)}/>
                                </span>
                                )}
                                <span style={{flex: '1 1 220px'}}>
                                    <Lbl text={t('campaign.projects_used_for_price_update')} help={t('onboarding.scheduledJobs.help.projects_used_for_price_update')} onL={onL} offL={offL}/>
                                    <MultiSelect value={draft.projects_used_for_price_update} options={scrapy_cloud_projects} optionLabel="label" optionValue="value"
                                                 display="chip" style={{width: '100%'}} onChange={(e) => setD('projects_used_for_price_update', e.value)}/>
                                </span>
                                <span style={{flex: '1 1 160px'}}>
                                    <Lbl text={t('campaign.rerun_failed_job_number')} help={t('onboarding.scheduledJobs.help.rerun_failed_job_number')}/>
                                    <InputNumber value={draft.rerun_failed_job_number} style={{width: '100%'}} onValueChange={(e) => setD('rerun_failed_job_number', e.value)}/>
                                </span>
                                <span style={{flex: '1 1 160px'}}>
                                    <Lbl text={t('campaign.rerun_failed_job_delay')} help={t('onboarding.scheduledJobs.help.rerun_failed_job_delay')}/>
                                    <InputNumber value={draft.rerun_failed_job_delay} style={{width: '100%'}} onValueChange={(e) => setD('rerun_failed_job_delay', e.value)}/>
                                </span>
                            </div>
                        </div>
                        )}
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default ScheduledJobsOnboardingStep;
